export type AttributeInstructions = {
    steps: string[],
    docUrl: string,
    docLabel: string,
    pdfGuide?: {url: string, label: string},
};

export const ATTRIBUTE_NAMES_CASE_SENSITIVE_MESSAGE = 'Attribute names are case-sensitive. These names must match exactly. If any required attribute is missing or named differently, users will not be able to sign in.';

const okta: AttributeInstructions = {
    docUrl: 'https://help.okta.com/en-us/content/topics/apps/define-attribute-statements.htm',
    docLabel: 'View Okta attribute statements documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Okta.pdf', label: 'Download Okta SSO Configuration Guide'},
    steps: [
        'Log in to your Okta Admin Console.',
        'Go to Applications -> Applications and select your SAML app.',
        'Select the Sign On tab.',
        'In the Attribute Statements section, expand Show legacy configuration. The Edit button is only visible after expanding this section.',
        'Click Edit.',
        'Add three rows with the exact names shown below:',
        'Set Name to `email` and Value to `user.email`. Name Format should stay as "unspecified"',
        'Set Name to `firstName` and Value to `user.firstName`. Name Format should stay as "unspecified"',
        'Set Name to `lastName` and Value to `user.lastName`. Name Format should stay as "unspecified"',
        'Save your changes.',
    ],
};

const microsoft: AttributeInstructions = {
    docUrl: 'https://learn.microsoft.com/en-us/entra/identity-platform/saml-claims-customization',
    docLabel: 'View Microsoft Entra ID claims documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Microsoft Entra.pdf', label: 'Download Microsoft Entra SSO Configuration Guide'},
    steps: [
        'Log in to the Microsoft Entra Admin Center.',
        'Go to Identity → Applications → Enterprise applications and select your app.',
        'Click Single sign-on in the left menu, then select SAML.',
        'In the Attributes & Claims section, click Edit.',
        'Click Add new claim for each attribute listed below.',
        'For `email`: set Name to `email`, leave Namespace blank, Source to Attribute, Source attribute to `user.mail`.',
        'For `firstName`: set Name to `firstName`, Source attribute to `user.givenname`.',
        'For `lastName`: set Name to `lastName`, Source attribute to `user.surname`.',
        'Click Save after adding each claim.',
    ],
};

const onelogin: AttributeInstructions = {
    docUrl: 'https://developers.onelogin.com/saml',
    docLabel: 'View OneLogin SAML documentation',
    steps: [
        'Log in to your OneLogin Admin Portal.',
        'Go to Applications → Applications and select your SAML app.',
        'Open the Parameters tab.',
        'Click Add Parameter for each attribute listed below.',
        'Enter the Field name exactly as shown (e.g. email), and check "Include in SAML assertion".',
        'Set the Value to the matching OneLogin user field: `Email` for `email`, `First Name` for `firstName`, `Last Name` for `lastName`.',
        'Click Save after adding each parameter.',
    ],
};

const google: AttributeInstructions = {
    docUrl: 'https://support.google.com/a/answer/6087519',
    docLabel: 'View Google Workspace documentation',
    pdfGuide: {url: '/idp-instructions/SSO Configuration Guide - Google Workspaces.pdf', label: 'Download Google Workspaces SSO Configuration Guide'},
    steps: [
        'Log in to the Google Admin Console.',
        'Go to Apps → Web and mobile apps and select your custom SAML app.',
        'Scroll to the Attribute mapping section.',
        'Click Add mapping for each attribute listed below.',
        'For `email`: select `Primary email` as the Google Directory attribute and enter `email` as the App attribute.',
        'For `firstName`: select `First Name` and enter `firstName` as the App attribute.',
        'For `lastName`: select `Last Name` and enter `lastName` as the App attribute.',
        'Click Save.',
    ],
};

const pingfederate: AttributeInstructions = {
    docUrl: 'https://docs.pingidentity.com/pingfederate/12.3/introduction_to_pingfederate/pf_attr_contract.html',
    docLabel: 'View PingFederate attribute contract documentation',
    steps: [
        'Log in to the PingFederate Admin Console.',
        'Go to SP Connections and select your connection.',
        'Navigate to Browser SSO → Configure → Attribute Contract.',
        'Add the three attribute names listed below: `email`, `firstName`, `lastName`.',
        'In the Contract Fulfillment step, map each attribute to the corresponding field in your user datastore (e.g. mail, givenName, sn).',
        'Click Save.',
    ],
};

const other: AttributeInstructions = {
    docUrl: '',
    docLabel: '',
    steps: [
        'Log in to your Identity Provider\'s admin console.',
        'Open the SAML application settings for PitchBook.',
        'Find the attribute statements, claims, or attribute mapping section.',
        'Add the three attributes listed below using the exact names shown.',
        'Map each attribute to the corresponding user profile field in your system (`email address`, `first name`, `last name`).',
        'Save your changes.',
    ],
};

export const ATTRIBUTE_INSTRUCTIONS: Record<string, AttributeInstructions> = {
    okta,
    microsoft,
    onelogin,
    google,
    pingfederate,
    other,
};
